
import packageInfo from "package.json";
import { createAuthenticator } from "~/modules/auth";
import { ContextManager } from "./modules/contextManager/ContextManager";
import { getAccountConfigData } from "./modules/config/AccountConfigDataGetter/getAccountConfigData";
import { createClient as fromModuleCreateClient } from "./modules/client/ClientProvider/createClient";
import { getPublicConfig as fromModuleGetPublicConfig } from "./modules/config/PublicConfigProvider/getPublicConfig";
import { getFeaturesConfig as fromModuleGetFeaturesConfig } from "./modules/config/FeaturesConfigDataGetter/getFeaturesConfig";
import { setLogLevel as fromModuleSetLogLevel } from "~/modules/logger/LoggerFactory/LoggerConfigImpl";
import { getAuthenticationConfig as fromModuleGetAuthenticationConfig } from "~/modules/auth/AuthenticationConfigGetter/getAuthenticationConfig";
import type { DeepPartial } from "./utils/DeepPartial";
import type { ClientOptions } from "./modules/client";
import type { LoggerLevel } from "./modules/logger";
import type { XOR } from "./utils/utilityTypes";
import type { AccountSidOption, RuntimeDomainOption, AccountConfigOptions } from "./modules/config";
import type { AuthenticationConfigParams } from "./modules/auth";

export type { SetWorkerAttributesOptions } from "~/modules/actions/Supervisor/SetWorkerAttributes/SetWorkerAttributes";
export { SetWorkerAttributes } from "~/modules/actions/Supervisor/SetWorkerAttributes/SetWorkerAttributes";
export type { SetWorkerActivityOptions } from "~/modules/actions/Supervisor/SetWorkerActivity/SetWorkerActivity";
export { SetWorkerActivity } from "~/modules/actions/Supervisor/SetWorkerActivity/SetWorkerActivity";

export type { SetAttributesOptions } from "~/modules/actions/Worker/SetAttributes/SetAttributes";
export { SetAttributes } from "~/modules/actions/Worker/SetAttributes/SetAttributes";

export type { SetCurrentActivityOptions } from "~/modules/actions/Worker/SetCurrentActivity/SetCurrentActivity";
export { SetCurrentActivity } from "~/modules/actions/Worker/SetCurrentActivity/SetCurrentActivity";

export type { SetTaskAttributesOptions } from "~/modules/actions/Worker/SetTaskAttributes/SetTaskAttributes";
export { SetTaskAttributes } from "~/modules/actions/Worker/SetTaskAttributes/SetTaskAttributes";

export { AcceptTask } from "~/modules/actions/Task/AcceptTask/AcceptTask";
export type { AcceptTaskOptions } from "~/modules/actions/Task/AcceptTask/AcceptTask";

export { RejectTask } from "~/modules/actions/Task/RejectTask/RejectTask";

export { CompleteTask } from "~/modules/actions/Task/CompleteTask/CompleteTask";

export { EndTask } from "~/modules/actions/Task/EndTask/EndTask";

export { WrapUpTask } from "~/modules/actions/Task/WrapUpTask/WrapUpTask";

export { CancelVoiceTaskTransfer } from "~/modules/actions/Task/CancelVoiceTaskTransfer/CancelVoiceTaskTransfer";

export type { TransferTaskOptions } from "~/modules/actions/Task/StartVoiceTaskTransfer/StartVoiceTaskTransfer";

export { StartVoiceTaskTransfer } from "~/modules/actions/Task/StartVoiceTaskTransfer/StartVoiceTaskTransfer";

export { StartOutboundCall } from "~/modules/actions/VoiceChannel/StartOutBoundCall/StartOutBoundCall";
export type { StartOutboundCallOptions } from "~/modules/actions/VoiceChannel/StartOutBoundCall/StartOutBoundCall";

export { StartExternalWarmVoiceTaskTransfer } from "~/modules/actions/VoiceConference/StartExternalWarmVoiceTaskTransfer/StartExternalWarmVoiceTaskTransfer";

export type { HoldParticipantOptions } from "~/modules/actions/VoiceConference/HoldParticipant/HoldParticipant";
export { HoldParticipant } from "~/modules/actions/VoiceConference/HoldParticipant/HoldParticipant";

export { UnholdParticipant } from "~/modules/actions/VoiceConference/UnholdParticipant/UnholdParticipant";

export { KickParticipant } from "~/modules/actions/VoiceConference/KickParticipant/KickParticipant";

export { EndConferenceForAll } from "~/modules/actions/VoiceConference/EndConferenceForAll/EndConferenceForAll";

export { MonitorCall } from "~/modules/actions/VoiceChannel/MonitorCall/MonitorCall";
export type { MonitorCallOptions } from "~/modules/actions/VoiceChannel/MonitorCall/MonitorCall";

export { AddVoiceEventListener } from "~/modules/actions/VoiceChannel/AddVoiceEventListener/AddVoiceEventListener";
export type { AddVoiceEventListenerOptions } from "~/modules/actions/VoiceChannel/AddVoiceEventListener/AddVoiceEventListener";

export type { Action } from "~/modules/actions/Action";

export type { SessionOptions } from "./modules/session";
export type { DeepPartial } from "./utils/DeepPartial";
export type { Without, XOR } from "./utils/utilityTypes";
export type {
    AccountConfig,
    AccountConfigProvider,
    AccountConfigKeys,
    AccountConfigData,
    IntegrationsConfig,
    OutboundFlows,
    SkillDefinition,
    QueueStatsConfiguration,
    PublicConfig,
    PublicConfigAttributes,
    PublicConfigProvider,
    AccountSidOption,
    RuntimeDomainOption,
    TaskQueue,
    WorkerChannel,
    DefaultQueueStatsConfiguration,
    QueueConfiguration,
    QueueChannelConfiguration,
    FeaturesConfigDataGetter,
    FeaturesConfig,
    FeaturesList,
    AccountConfigDataGetter,
    AccountConfigOptions,
    ChannelConfig,
    CbmAttachments,
    DebuggerIntegration,
    FlexUiStatusReport,
    CitrixVoiceVdi
} from "./modules/config";
export type { Logger, LoggerOptions, LoggerLevel, LoggerLevelNumbers } from "./modules/logger";
export type { Client, ClientProvider, ClientOptions, ClientConfigType } from "./modules/client";
export { ClientEvent } from "./modules/client";
export type {
    Authenticator,
    AuthenticatorFactory,
    IdpConfig,
    Role,
    TokenData,
    AppType,
    TokenRefreshResult,
    AuthenticationConfigParams,
    AuthenticationConfigResponse,
    AuthenticationConfig,
    LoginDetailsResponse,
    ExchangeTokenParams,
    FlexAuthToken
} from "./modules/auth";
export type { EventPublisher } from "./modules/events";
export type { TelemetryOptions } from "./modules/telemetry";
export { hasPermission } from "~/modules/auth/Permission";
export type {
    VirtualAgentDataApi,
    VirtualAgentData,
    VirtualAgentDataSentences
} from "~/modules/virtualAgentData/Definitions";

export type {
    ProfileConnectorApis as ProfileConnector,
    ProfilesResponse,
    Profile,
    ProfileAddress,
    ProfileContact,
    ProfileAttribute,
    RankedProfile,
    FindProfilesRequest,
    IdentifiersResponse,
    TraitsResponse,
    Identifier,
    Trait
} from "~/modules/profileConnector/Definitions";

export type { VoiceCall, HoldCallOptions } from "~/modules/VoiceCall";

export type { DataClientOptions, GetToken, TypePolicies } from "~/modules/dataClient";

export type { TaskRouterOptions } from "./packages/taskrouter/TaskRouterOptions";

export type {
    AddVoiceParticipantResponse,
    CbmParticipant,
    ParticipantResponse,
    ParticipantType,
    MediaChannelType,
    RoutingProperties,
    MediaProperties,
    ParticipantEventData,
    RoutingData
} from "./packages/cbm";

export type { VoiceOptions } from "./modules/voice/voiceOptions";

export * from "./packages/taskrouter/types";


const ctx = ContextManager.newInstance();













export const auth = (accountSid: string) => createAuthenticator(ctx)(accountSid);















export const setLogLevel = (level: LoggerLevel) => fromModuleSetLogLevel(ctx)(level);



















export const getPublicConfig = (option: XOR<AccountSidOption, RuntimeDomainOption>) =>
    fromModuleGetPublicConfig(ctx)(option);




















export const getAccountConfig = (sessionToken: string, options?: AccountConfigOptions) =>
    getAccountConfigData(ctx)(sessionToken, options);



















export const getAuthenticationConfig = (config: AuthenticationConfigParams) =>
    fromModuleGetAuthenticationConfig(ctx)(config);
















export const getFeaturesConfig = (token: string, uiVersion?: string) =>
    fromModuleGetFeaturesConfig(ctx)(token, uiVersion);





















export const createClient = (token: string, userOptions?: DeepPartial<ClientOptions>) =>
    fromModuleCreateClient(ctx)(token, userOptions);






export const version: string = packageInfo.version;

export type { FlexSdkErrorMetadata } from "./modules/error";
export { FlexSdkError, ErrorCode, ErrorSeverity } from "./modules/error";
